import { z } from 'zod';
import { AuditMeta, DocId, IsoTimestamp, TenantId } from '../common.js';
/**
 * Task envelope. Cloud Tasks + Pub/Sub handle the active queue; Firestore
 * holds final state + audit trail + admin-visible progress.
 *
 * Firestore path: tasks/{taskId}
 */
export const TaskType = z.enum([
    'ingest_document',
    'crawl_ring2_source',
    'refresh_provider_geocode',
    'reindex_document_version',
]);
export const TaskStatus = z.enum(['pending', 'running', 'completed', 'failed']);
/**
 * Params vary per task type. Validated at the task handler boundary.
 */
export const IngestDocumentParams = z.object({
    documentId: DocId,
    sourceUri: z.string(),
});
export const CrawlRing2SourceParams = z.object({
    sourceDomain: z.string().min(1).max(200),
    startUrl: z.string().url(),
});
export const RefreshProviderGeocodeParams = z.object({
    providerId: DocId,
});
export const ReindexDocumentVersionParams = z.object({
    documentId: DocId,
    version: z.string().min(1).max(32),
});
export const TaskParams = z.discriminatedUnion('type', [
    z.object({ type: z.literal('ingest_document'), params: IngestDocumentParams }),
    z.object({ type: z.literal('crawl_ring2_source'), params: CrawlRing2SourceParams }),
    z.object({ type: z.literal('refresh_provider_geocode'), params: RefreshProviderGeocodeParams }),
    z.object({ type: z.literal('reindex_document_version'), params: ReindexDocumentVersionParams }),
]);
export const TaskSchema = AuditMeta.extend({
    id: DocId,
    tenantId: z.union([TenantId, z.literal('_platform')]),
    type: TaskType,
    /**
     * Raw params JSON. Cast per-task-type via `TaskParams.parse({ type, params })`
     * at the handler boundary. Kept as unknown record here so the Firestore doc
     * survives schema evolution.
     */
    params: z.record(z.unknown()),
    status: TaskStatus.default('pending'),
    attempts: z.number().int().nonnegative().default(0),
    /**
     * Cloud Tasks enqueues a task with a scheduled execution time. For
     * deferred work (e.g. `runAt` in 2 hours) the backend sets this.
     */
    scheduledAt: IsoTimestamp.optional(),
    startedAt: IsoTimestamp.optional(),
    completedAt: IsoTimestamp.optional(),
    error: z
        .object({
        code: z.string().max(100),
        message: z.string().max(2000),
        stack: z.string().optional(),
    })
        .optional(),
});
/**
 * Tightly-typed parse helper — useful at the task handler boundary.
 * Throws if params don't match the type's schema.
 */
export function parseTaskParams(type, params) {
    return TaskParams.parse({ type, params });
}
//# sourceMappingURL=task.js.map